import {
  type ExoSkeletonEventNames,
  EXO_SKELETON_RUNTIME_SCOPE,
} from './consts';
import {
  createUniqueRequestId,
  ExoSkeletonRuntime,
  getExoSkeletonRuntime,
} from './internal';

export interface ExoSkeletonRuntimeDataEvent<
  TYPE extends ExoSkeletonEventNames,
  DATA,
> {
  requestId: string;
  type: TYPE;
  data: DATA;
  eventTimestampUTC: number;
}

export function createRuntimeDataEvent<
  TYPE extends ExoSkeletonEventNames,
  DATA,
>(type: TYPE, data: DATA): ExoSkeletonRuntimeDataEvent<TYPE, DATA> {
  return {
    requestId: createUniqueRequestId(type),
    type,
    data,
    eventTimestampUTC: new Date(new Date().toUTCString()).getTime(),
  };
}

export function isInExoSkeleton() {
  return Boolean(window[EXO_SKELETON_RUNTIME_SCOPE]?.available);
}

function registerExoSkeletonListener<TYPE extends ExoSkeletonEventNames>(
  type: TYPE,
) {
  const runtime = getExoSkeletonRuntime();

  runtime.listeners = Array.from(new Set([...(runtime.listeners ?? []), type]));
}

export function removeExoSkeletonListener<TYPE extends ExoSkeletonEventNames>(
  type: TYPE,
) {
  const runtime = getExoSkeletonRuntime();

  if (!runtime.listeners) {
    runtime.listeners = [] as ExoSkeletonRuntime['listeners'];
  }

  runtime.listeners = runtime.listeners!.filter(
    (listener) => listener !== type,
  );
}

export function hasExoSkeletonListenerInitialized<
  TYPE extends ExoSkeletonEventNames,
>(type: TYPE) {
  const runtime = getExoSkeletonRuntime();

  if (!runtime.listeners) {
    runtime.listeners = [] as ExoSkeletonRuntime['listeners'];
  }

  return runtime.listeners!.includes(type);
}

export function createPostMessageEventHandler<
  DATA,
  TYPE extends ExoSkeletonEventNames,
>(type: TYPE, callback: ({ data }: MessageEvent<DATA>) => void) {
  registerExoSkeletonListener(type);
  window.addEventListener('message', callback);

  return {
    dispose() {
      removeExoSkeletonListener(type);
      window.removeEventListener('message', callback);
    },
  };
}

export function sendRuntimeDataEvent<TYPE extends ExoSkeletonEventNames, DATA>(
  type: TYPE,
  data: DATA,
): Promise<ExoSkeletonRuntimeDataEvent<TYPE, DATA>> {
  const targetOrigin = window.location.origin;

  const event = createRuntimeDataEvent(type, data);

  return new Promise<typeof event>((resolve) => {
    const listenerCallback: ({ data }: MessageEvent<typeof event>) => void = ({
      data: eventData,
    }) => {
      if (eventData?.requestId === event.requestId) {
        resolve(eventData);
        window.removeEventListener('message', listenerCallback);
      }
    };

    window.addEventListener('message', listenerCallback);
    try {
      window.postMessage(event, targetOrigin);
    } catch (e) {
      const originalEventData = event.data;
      event.data = JSON.parse(JSON.stringify(event.data));

      window.postMessage(event, targetOrigin);

      console.error(
        'Failed to send parse "postMessage" data, fallback to "JSON.parse(JSON.stringify(...))"',
        e,
        event,
        originalEventData,
      );
    }
  });
}
